import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { CaretLeft } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';

const Section = ({ children, delay = 0 }) => (
  <motion.section
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.6, delay }}
    className="mb-14"
  >
    {children}
  </motion.section>
);

const About = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen noise-bg" data-testid="about-page">
      {user ? <Navbar /> : (
        <div className="max-w-7xl mx-auto px-6 pt-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-zinc-400 hover:text-gold transition-colors"
            data-testid="about-back-btn"
          >
            <CaretLeft size={16} weight="bold" /> Back
          </button>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero quote */}
        <motion.blockquote
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-20"
          data-testid="about-hero-quote"
        >
          <p className="text-2xl md:text-4xl font-unbounded font-bold leading-snug bg-gradient-to-r from-yellow-300 via-gold to-yellow-500 bg-clip-text text-transparent">
            &ldquo;Empowering the next generation with the confidence to invest, learn, and grow.&rdquo;
          </p>
        </motion.blockquote>

        {/* Meet the Founder */}
        <Section>
          <h2 className="text-3xl md:text-4xl font-unbounded font-bold text-white mb-2">Meet the Founder</h2>
          <h3 className="text-xl md:text-2xl text-gold font-semibold mb-6" data-testid="about-founder-name">
            Neil Prabhu
          </h3>
          <div className="space-y-4 text-zinc-300 text-base md:text-lg leading-relaxed">
            <p>Hi, I&apos;m Neil, the founder of TeenVest.</p>
            <p>
              Growing up, I realized that while students spend years learning subjects like math and
              science, very few are taught how investing, wealth creation, and financial decision-making
              actually work. Yet these are skills that shape our futures.
            </p>
            <p>I built TeenVest to change that.</p>
            <p>
              TeenVest is a place where teenagers can safely experience investing using virtual money,
              receive AI-powered guidance, compete with friends, and develop financial confidence
              before entering the real world.
            </p>
            <p>
              My goal isn&apos;t simply to build another investing app — it&apos;s to help create a
              generation that understands money instead of fearing it.
            </p>
          </div>
        </Section>

        {/* Our Story */}
        <Section>
          <h2 className="text-3xl md:text-4xl font-unbounded font-bold text-white mb-8">Our Story</h2>

          <div className="glass-card p-6 md:p-8 mb-6">
            <h3 className="text-xl md:text-2xl text-gold font-semibold mb-4">The Problem</h3>
            <div className="space-y-4 text-zinc-300 text-base md:text-lg leading-relaxed">
              <p>
                Traditional education prepares students for exams, but often leaves them unprepared for
                important financial decisions. Investing can feel intimidating — filled with unfamiliar
                terms, risks, and information that seems designed for adults.
              </p>
              <p>
                Many young people only begin learning about investing after they start earning, often
                through trial and error.
              </p>
              <p className="text-white font-semibold">
                We believe financial education should begin much earlier.
              </p>
            </div>
          </div>

          <div className="glass-card p-6 md:p-8">
            <h3 className="text-xl md:text-2xl text-gold font-semibold mb-4">Our Mission</h3>
            <div className="space-y-4 text-zinc-300 text-base md:text-lg leading-relaxed">
              <p>Our mission is simple:</p>
              <p className="text-white text-lg md:text-xl font-semibold border-l-2 border-gold pl-4 italic">
                To make investing education accessible, engaging, and completely risk-free for every teenager.
              </p>
              <p>
                We combine AI, virtual trading, competition, and community learning to transform
                investing from something confusing into something exciting.
              </p>
            </div>
          </div>
        </Section>

        {/* Why TeenVest? */}
        <Section>
          <h2 className="text-3xl md:text-4xl font-unbounded font-bold text-white mb-6">Why TeenVest?</h2>
          <div className="space-y-4 text-zinc-300 text-base md:text-lg leading-relaxed mb-6">
            <p>Unlike traditional investing apps, TeenVest isn&apos;t built to help people trade with real money.</p>
            <p className="text-white text-xl font-semibold">It&apos;s built to help people learn.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              'Virtual investing',
              'AI investment assistant',
              'Competitive game modes',
              'Community discussions',
              'Financial education',
              'Risk-free learning',
            ].map((item) => (
              <div key={item} className="flex items-center gap-3 p-3 glass-card">
                <span className="text-emerald-400 text-xl">✔</span>
                <span className="text-zinc-200">{item}</span>
              </div>
            ))}
          </div>
          <p className="mt-6 text-zinc-400 italic">Every feature exists to teach, not just entertain.</p>
        </Section>

        {/* Our Roadmap */}
        <Section>
          <h2 className="text-3xl md:text-4xl font-unbounded font-bold text-white mb-2">Our Roadmap</h2>
          <p className="text-zinc-400 mb-8">TeenVest is only getting started.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="glass-card p-6">
              <h3 className="text-xl text-gold font-semibold mb-4">Current</h3>
              <ul className="space-y-2 text-zinc-200">
                {['Virtual Trading', 'AI Analyst', 'Community', 'Leaderboards', 'Wolf Mode', 'Chaos Mode', 'Learn Mode'].map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <span className="text-gold">&rarr;</span> {f}
                  </li>
                ))}
              </ul>
            </div>
            <div className="glass-card p-6">
              <h3 className="text-xl text-gold font-semibold mb-4">Coming Soon</h3>
              <ul className="space-y-2 text-zinc-200">
                <li className="flex items-center gap-2">📱 Mobile App</li>
                <li className="flex items-center gap-2">🤖 A finance app that teaches more than investing</li>
                <li className="flex items-center gap-2">🏅 Bringing TeenVest to schools</li>
                <li className="flex items-center gap-2">📊 Portfolio Analytics</li>
                <li className="flex items-center gap-2">🌍 Global Teen Community</li>
                <li className="flex items-center gap-2"><span className="text-gold">&rarr;</span> Premium Plans</li>
              </ul>
            </div>
          </div>
        </Section>

        {/* Our Values */}
        <Section>
          <h2 className="text-3xl md:text-4xl font-unbounded font-bold text-white mb-8">Our Values</h2>
          <div className="space-y-4">
            {[
              { title: 'Learning First',   body: 'Knowledge comes before profits.' },
              { title: 'Innovation',       body: 'Using AI and technology to transform financial education.' },
              { title: 'Community',        body: 'Learning is better together.' },
              { title: 'Accessibility',    body: 'Financial education should be available to everyone.' },
              { title: 'Long-Term Thinking', body: 'Good investing is built on patience, discipline, and understanding.' },
            ].map((v) => (
              <div key={v.title} className="flex items-start gap-4 glass-card p-5">
                <span className="text-gold text-xl">&rarr;</span>
                <div>
                  <h4 className="text-white font-semibold text-lg">{v.title}</h4>
                  <p className="text-zinc-400">{v.body}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Final CTA */}
        <Section>
          <div className="text-center glass-card p-10">
            <h2 className="text-3xl md:text-4xl font-unbounded font-bold text-white mb-3">Ready?</h2>
            <p className="text-zinc-300 text-lg mb-6">Start your investing journey today.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to={user ? '/dashboard' : '/'}
                className="px-6 py-3 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold hover:scale-105 transition-transform shadow-glow"
                data-testid="about-cta-get-started"
              >
                Get Started
              </Link>
              {!user && (
                <Link
                  to="/"
                  className="px-6 py-3 rounded-full border-2 border-white/20 text-white font-bold hover:bg-white/10 transition-colors"
                  data-testid="about-cta-login"
                >
                  Log in
                </Link>
              )}
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default About;
